import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import API from "../api";
import { COMPETITION_OPTIONS } from "../constants/competitionPrefs";
import { CURRENT_SEASON } from "../constants/season";

const RECENT_KEY = "p90_recent_search_v1";
const FAVORITE_KEY = "p90_favorite_search_v1";

function readRecent() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(raw) ? raw.slice(0, 5) : [];
  } catch {
    return [];
  }
}

function remember(item) {
  const next = [item, ...readRecent().filter((row) => row.to !== item.to)].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function readFavorites() {
  try {
    const raw = JSON.parse(localStorage.getItem(FAVORITE_KEY) || "[]");
    return Array.isArray(raw) ? raw.slice(0, 8) : [];
  } catch {
    return [];
  }
}

export default function CommandSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [players, setPlayers] = useState([]);
  const [favorites, setFavorites] = useState(readFavorites);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("p90-open-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("p90-open-search", onOpen);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    API.get("/leaderboard", { params: { competition: "premier_league", season: CURRENT_SEASON } })
      .then((res) => {
        if (!cancelled) setPlayers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setPlayers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const saved = favorites.map((row) => ({ ...row, group: "Favorites" }));
    const recent = readRecent()
      .filter((row) => !favorites.some((fav) => fav.to === row.to))
      .map((row) => ({ ...row, group: "Recent" }));
    if (!q) return [...saved, ...recent];
    const comps = COMPETITION_OPTIONS.filter((c) => c.label.toLowerCase().includes(q)).map((c) => ({
      label: c.label,
      hint: "Competition",
      to: `/fixtures?competition=${c.id}`,
      group: "Competitions",
    }));
    const people = players
      .filter((row) => String(row.username || "").toLowerCase().includes(q))
      .slice(0, 6)
      .map((row) => ({
        label: row.username,
        hint: "Player",
        to: `/leaderboard?player=${encodeURIComponent(row.username)}`,
        group: "Players",
      }));
    const matches = [
      {
        label: `Matches for “${query.trim()}”`,
        hint: "Fixtures",
        to: `/fixtures?q=${encodeURIComponent(query.trim())}`,
        group: "Matches",
      },
    ];
    return [...comps, ...matches, ...people];
  }, [query, players, favorites]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const go = (item) => {
    if (!item) return;
    remember({ label: item.label, to: item.to, hint: item.hint });
    navigate(item.to);
    close();
  };

  const toggleFavorite = (item) => {
    setFavorites((current) => {
      const saved = { label: item.label, to: item.to, hint: item.hint };
      const next = current.some((row) => row.to === item.to)
        ? current.filter((row) => row.to !== item.to)
        : [saved, ...current].slice(0, 8);
      localStorage.setItem(FAVORITE_KEY, JSON.stringify(next));
      return next;
    });
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[12vh]">
      <button type="button" className="absolute inset-0 bg-ink/40" aria-label="Close search" onClick={close} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search Predict90"
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-line bg-white shadow-bar"
      >
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") close();
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive((index) => Math.min(index + 1, Math.max(results.length - 1, 0)));
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive((index) => Math.max(index - 1, 0));
            }
            if (event.key === "Enter") go(results[active]);
          }}
          placeholder="Matches, teams, competitions, players"
          aria-label="Search matches, teams, competitions, and players"
          aria-activedescendant={results[active] ? `search-result-${active}` : undefined}
          className="h-12 w-full border-b border-line px-4 text-[15px] text-ink outline-none placeholder:text-gray-400"
        />
        <ul className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 ? (
            <li className="px-4 py-6 text-sm text-muted">
              {query.trim()
                ? "Nothing matches. Star a result to keep it here."
                : "Type a team, a player, or a competition."}
            </li>
          ) : (
            results.map((item, index) => {
              const saved = favorites.some((row) => row.to === item.to);
              return (
                <li key={`${item.group}-${item.to}-${item.label}`} className="flex items-stretch">
                  <button
                    id={`search-result-${index}`}
                    type="button"
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(item)}
                    className={`flex min-h-11 min-w-0 flex-1 items-center justify-between gap-3 px-4 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                      index === active ? "bg-brand-soft" : ""
                    }`}
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-ink">{item.label}</span>
                    <span className="shrink-0 text-[12px] text-muted">{item.hint || item.group}</span>
                  </button>
                  <button
                    type="button"
                    aria-pressed={saved}
                    aria-label={saved ? `Remove ${item.label} from favorites` : `Save ${item.label} to favorites`}
                    onClick={() => toggleFavorite(item)}
                    className="w-11 shrink-0 text-lg text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
                  >
                    {saved ? "★" : "☆"}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <p className="border-t border-line px-4 py-2 text-[12px] text-muted">↑↓ to move · Enter to open · Esc to close</p>
      </div>
    </div>,
    document.body
  );
}
